import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { useSelector } from 'react-redux';

import { THEMES, THEME_LIST, DEFAULT_THEME_KEY, THEME_RECONCILE_FLAG } from '../config/themes';
import {
  applyThemeColors,
  getActiveThemeKey,
  getBootThemeKey,
  getStoredThemeKey,
  setActiveThemeKey
} from '../config/default';
import { buildTheme } from '../theme';
import { updateUserTheme } from '../services/services';

const ThemeModeContext = createContext({
  themeKey: DEFAULT_THEME_KEY,
  themes: THEME_LIST,
  nonce: 0,
  setThemeKey: () => {}
});

export const useThemeMode = () => useContext(ThemeModeContext);

// Session flag separating a "fresh login" (adopt the user's backend value) from
// an "in-session reload after a switch" (trust this device's local choice). Set
// by login (signIn) and below; wiped on logout. See config/themes for details.
const RECONCILE_FLAG = THEME_RECONCILE_FLAG;

// CSS custom properties consumed by static stylesheets (index.css, etc.) that
// can't read the JS `Colors` object. Each maps to a key of a theme's color set.
const CSS_VAR_MAP = {
  '--bh-bg': 'BG_BLACK',
  '--bh-surface': 'NAV_BLACK',
  '--bh-surface-alt': 'Canvas_Left_BG',
  '--bh-surface-raised': 'Canvas_BG',
  '--bh-text': 'WHITE',
  '--bh-text-muted': 'LIGHTGREY',
  '--bh-text-disabled': 'DEEPGREY',
  '--bh-accent': 'TOURQUISE',
  '--bh-accent-strong': 'DARKBLUE',
  '--bh-border': 'WHITE',
  '--bh-line': 'DEEPGREY',
  '--bh-seam': 'Canvas_Seam',
  '--bh-scrollbar-thumb': 'WHITE'
};

const applyCssVars = (key) => {
  const def = THEMES[key] || THEMES[DEFAULT_THEME_KEY];
  const root = document?.documentElement;
  if (!root) return;
  Object.entries(CSS_VAR_MAP).forEach(([cssVar, colorKey]) => {
    if (def.colors[colorKey]) root.style.setProperty(cssVar, def.colors[colorKey]);
  });
  // Expose the active mode so static stylesheets can scope light-only overrides
  // (e.g. third-party widgets like react-datepicker that read CSS, not `Colors`).
  if (def.mode) root.setAttribute('data-bh-mode', def.mode);
};

export const ThemeModeProvider = ({ children }) => {
  const themeKey = getActiveThemeKey();

  // The signed-in profile object. We depend on the OBJECT REFERENCE (not a
  // derived `Boolean(user)` or the `user.theme` string) so the reconcile effect
  // below re-fires when a FRESH login lands a new profile. This matters because
  // redux-persist here uses localStorage (see store.js) and persists `userInfo`,
  // while the auth token lives in sessionStorage (cleared on tab-close). After a
  // session that ended without an explicit logout, the app boots on the login
  // screen with `user` ALREADY rehydrated from localStorage — so a boolean
  // "is loaded" and the theme string are both unchanged across the next login.
  // Keying off the object reference (login dispatches a fresh API object) is what
  // makes a same-user re-login re-run this effect once the token is set, instead
  // of silently skipping the theme reconcile/reload.
  const user = useSelector((state) => state?.userInfo?.userInfo?.user);
  // Theme stored on the user's backend profile — lets the choice follow them
  // across devices once the backend persists it.
  const remoteTheme = user?.theme;

  // Bumped after the live palette is swapped so the whole routed tree
  // (`<Routes key={nonce}>` in App.js) remounts and repaints with the
  // freshly-mutated `Colors`. Without this, inline styles that read `Colors.X`
  // would keep the pre-swap values until some unrelated render.
  const [nonce, setNonce] = useState(0);
  // Tracks the last applied key so we only remount on a REAL theme change — not
  // on the initial mount (which would needlessly remount every page / re-fire
  // their data fetches on first load).
  const appliedThemeKey = useRef(themeKey);

  // Sync the static-stylesheet CSS variables to the active theme. Theme CHANGES
  // go through `commitTheme` (full reload), so on a normal boot this just seeds
  // the CSS vars once with the booted theme. The routed-tree remount (nonce) is
  // kept as a safety net for the rare case `getActiveThemeKey()` flips without a
  // reload (e.g. a token landing at login before any reload).
  useEffect(() => {
    applyCssVars(themeKey);
    if (appliedThemeKey.current !== themeKey) {
      appliedThemeKey.current = themeKey;
      setNonce((n) => n + 1);
    }
  }, [themeKey]);

  // Apply a theme choice by persisting it and doing a FULL page reload. The
  // reload re-boots the app with the new theme so `Colors` — and every
  // module-scope style / static CSS that reads it at import time — freezes at
  // the CORRECT palette. (In-place live swapping left those import-time captures
  // showing the previous theme, e.g. dark surfaces under light-theme text.)
  const commitTheme = useCallback(async (key, persistRemote) => {
    // Never theme-switch while logged out — auth screens are always dark, and
    // `getActiveThemeKey` pins to dark without a token.
    if (!sessionStorage.getItem('token')) return;
    // No-op only if the requested theme is the one this page actually BOOTED
    // with (what `Colors` + module-scope captures are frozen to). Comparing
    // against `getActiveThemeKey()` is wrong here: it reads localStorage and so
    // already returns the stored theme the instant a token lands at login, even
    // though the live palette is still the dark boot value — that mismatch is
    // exactly why login looked half-themed.
    if (!THEMES[key] || key === getBootThemeKey()) return;
    setActiveThemeKey(key); // localStorage = source of truth read at the next boot
    // Persist to the backend BEFORE reloading — a reload cancels in-flight
    // requests, so fire-and-forget would be lost. Best-effort: reload regardless.
    if (persistRemote) {
      try {
        await updateUserTheme(key);
      } catch (e) {
        // ignore — the local choice still applies after the reload.
      }
    }
    window.location.reload();
  }, []);

  // Reconcile the live theme with the user's saved choice, once per session.
  // Runs as soon as the signed-in profile lands in redux (login).
  //
  // Why a fresh-login vs in-session distinction matters:
  //   • Fresh login (flag absent — logout cleared it): honor the backend/DB
  //     value, so the saved theme follows the user back in (and across devices).
  //   • In-session (flag present, e.g. right after a switch): trust THIS
  //     device's local choice and never let a stale redux value revert it.
  const reconciled = useRef(false);
  useEffect(() => {
    if (reconciled.current) return;
    // While logged out the app is pinned to dark (getActiveThemeKey forces it),
    // so there's nothing to reconcile until a token + profile exist.
    if (!sessionStorage.getItem('token')) return;
    if (!user) return;
    reconciled.current = true;

    const local = getStoredThemeKey();
    const freshLogin = !sessionStorage.getItem(RECONCILE_FLAG);
    sessionStorage.setItem(RECONCILE_FLAG, '1');

    const remoteValid = remoteTheme && THEMES[remoteTheme] ? remoteTheme : null;
    // Fresh login: the user's saved (backend) theme, else default. We deliberately
    // do NOT fall back to `local` here — theme is a per-USER setting, and `local`
    // is whatever theme this DEVICE last ran, which may belong to a DIFFERENT
    // account (the one that logged out before this one). Using it leaked the prior
    // account's theme onto an account with no saved theme.
    // In-session reload (after a switch on THIS account): trust local — the switch
    // just wrote it, and it belongs to the still-signed-in user.
    const desired = freshLogin
      ? remoteValid || DEFAULT_THEME_KEY
      : local || DEFAULT_THEME_KEY;

    // Reload only when the target differs from what this page actually BOOTED
    // with (getBootThemeKey) — NOT getActiveThemeKey, which already reads the
    // stored theme from localStorage the moment the token lands and would make
    // this no-op while the live palette is still the dark boot value. After the
    // reload the page boots WITH a token, so getBootThemeKey === desired and this
    // no-ops — no reload loop. (RECONCILE_FLAG, set above, also flips freshLogin
    // off post-reload as a second guard.)
    if (desired !== getBootThemeKey()) commitTheme(desired, false);
  }, [user, remoteTheme, commitTheme]);

  const setThemeKey = useCallback((key) => commitTheme(key, true), [commitTheme]);

  const muiTheme = useMemo(() => {
    // Swap the live `Colors` palette in the SAME render pass that rebuilds the
    // MUI theme — before children (and their makeStyles / styled sheets) read it
    // — so the new palette is visible immediately with no reload, no stale frame.
    applyThemeColors(themeKey);
    return buildTheme(themeKey);
  }, [themeKey]);

  return (
    <ThemeModeContext.Provider value={{ themeKey, themes: THEME_LIST, nonce, setThemeKey }}>
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeProvider;
